import { useState, useEffect, useCallback } from "react";

interface UseDragAndDropProps {
	onFilesDrop: (files: FileList) => void;
	onDropSuccess?: (fileCount: number) => void;
}

interface UseDragAndDropReturn {
	isDragOver: boolean;
}

export const useDragAndDrop = ({
	onFilesDrop,
	onDropSuccess,
}: UseDragAndDropProps): UseDragAndDropReturn => {
	const [isDragOver, setIsDragOver] = useState(false);

	const handleDragOver = useCallback((e: DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
	}, []);

	const handleDragEnter = useCallback((e: DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (e.dataTransfer?.items && e.dataTransfer.items.length > 0) {
			setIsDragOver(true);
		}
	}, []);

	const handleDragLeave = useCallback((e: DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		// Only hide drag overlay if we're leaving the viewport entirely
		if (e.clientX === 0 && e.clientY === 0) {
			setIsDragOver(false);
		}
	}, []);

	const handleDrop = useCallback(
		(e: DragEvent) => {
			e.preventDefault();
			e.stopPropagation();
			setIsDragOver(false);

			if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
				onFilesDrop(e.dataTransfer.files);
				onDropSuccess?.(e.dataTransfer.files.length);
			}
		},
		[onFilesDrop, onDropSuccess],
	);

	useEffect(() => {
		// Add event listeners to document
		document.addEventListener("dragover", handleDragOver);
		document.addEventListener("dragenter", handleDragEnter);
		document.addEventListener("dragleave", handleDragLeave);
		document.addEventListener("drop", handleDrop);

		return () => {
			// Cleanup event listeners
			document.removeEventListener("dragover", handleDragOver);
			document.removeEventListener("dragenter", handleDragEnter);
			document.removeEventListener("dragleave", handleDragLeave);
			document.removeEventListener("drop", handleDrop);
		};
	}, [handleDragOver, handleDragEnter, handleDragLeave, handleDrop]);

	return {
		isDragOver,
	};
};
